const API_BASE_URL = String(import.meta.env.VITE_API_BASE_URL || "")
  .trim()
  .replace(/\/+$/, "");

const NGROK_SKIP_HEADER = { "ngrok-skip-browser-warning": "true" };
const MAX_IMAGE_BLOB_FETCHES = 5;
let activeImageBlobFetches = 0;
const imageBlobQueue = [];

function apiPath(path) {
  if (!API_BASE_URL) {
    throw new Error("VITE_API_BASE_URL is missing");
  }
  return `${API_BASE_URL}${path}`;
}

function supabaseAdminHeaders(accessToken) {
  return {
    Authorization: `Bearer ${accessToken}`,
    "x-supabase-access-token": accessToken,
  };
}

async function request(path, options = {}) {
  let response;
  try {
    response = await fetch(apiPath(path), {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...NGROK_SKIP_HEADER,
        ...(options.headers || {}),
      },
    });
  } catch {
    throw new Error(
      "Backend API not reachable. Check backend, ngrok, VITE_API_BASE_URL, and CORS."
    );
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401 && path.startsWith("/api/admin/")) {
      throw new Error("Invalid gallery admin password");
    }
    throw new Error(data.error || `Gallery request failed (${response.status})`);
  }
  return data;
}

export const isBackendApiUrl = (url) =>
  Boolean(API_BASE_URL && String(url || "").startsWith(`${API_BASE_URL}/api/`));

async function withImageBlobLimit(operation, priority = false) {
  if (activeImageBlobFetches >= MAX_IMAGE_BLOB_FETCHES) {
    await new Promise((resolve) => {
      if (priority) imageBlobQueue.unshift(resolve);
      else imageBlobQueue.push(resolve);
    });
  }
  activeImageBlobFetches += 1;
  try {
    return await operation();
  } finally {
    activeImageBlobFetches -= 1;
    const next = imageBlobQueue.shift();
    if (next) next();
  }
}

export async function fetchImageBlobUrl(url, options = {}) {
  const headers = isBackendApiUrl(url) ? NGROK_SKIP_HEADER : {};

  let response;
  try {
    response = await withImageBlobLimit(() => fetch(url, { headers }), Boolean(options.priority));
  } catch {
    throw new Error(
      "Backend API not reachable. Check backend, ngrok, VITE_API_BASE_URL, and CORS."
    );
  }

  if (!response.ok) {
    throw new Error(`Gallery media request failed (${response.status})`);
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  // Read corrected dimensions from backend response headers (set after EXIF
  // rotation) so portrait photos get the right aspect ratio immediately.
  const naturalWidth = parseInt(response.headers.get("X-Photo-Width") || "0", 10) || null;
  const naturalHeight = parseInt(response.headers.get("X-Photo-Height") || "0", 10) || null;
  return { objectUrl, naturalWidth, naturalHeight };
}

export async function downloadBackendFile(url, filename = "download") {
  if (!isBackendApiUrl(url)) {
    window.location.href = url;
    return;
  }

  const { objectUrl } = await fetchImageBlobUrl(url);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export async function downloadAdminCsv(password, path, filename = "registrations.csv") {
  let response;
  try {
    response = await fetch(apiPath(path), {
      headers: {
        ...NGROK_SKIP_HEADER,
        "x-gallery-admin-password": password,
      },
    });
  } catch {
    throw new Error(
      "Backend API not reachable. Check backend, ngrok, VITE_API_BASE_URL, and CORS."
    );
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `CSV export failed (${response.status})`);
  }
  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export const getPublicGalleries = () => request("/api/gallery");
export const verifyGalleryPin = (slug, pin) =>
  request(`/api/gallery/${slug}/verify`, { method: "POST", body: JSON.stringify({ pin }) });
export const getGalleryPhotos = (slug, token, limit = 30, offset = 0, folderId = "", bucket = "") =>
  request(`/api/gallery/${slug}/photos?limit=${limit}&offset=${offset}${folderId ? `&folderId=${encodeURIComponent(folderId)}` : ""}${bucket ? `&bucket=${encodeURIComponent(bucket)}` : ""}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
export const getGalleryAlbums = (slug, token) =>
  request(`/api/gallery/${slug}/albums`, { headers: { Authorization: `Bearer ${token}` } });
export const getGalleryShareLink = (shareToken) =>
  request(`/api/gallery/share/${shareToken}`);
export const verifyGallerySharePin = (shareToken, pin) =>
  request(`/api/gallery/share/${shareToken}/verify-pin`, {
    method: "POST",
    body: JSON.stringify({ pin }),
  });
export const registerGalleryShareAccess = (shareToken, payload) =>
  request(`/api/gallery/share/${shareToken}/register`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
export const verifyGalleryAdminPassword = (password) =>
  request("/api/admin/verify", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
export const importAdminDriveFolderMedia = (folderUrlOrId, accessToken) =>
  request("/api/admin/drive-folder-media", {
    method: "POST",
    headers: supabaseAdminHeaders(accessToken),
    body: JSON.stringify({ folderUrlOrId }),
  });
export const importClientGalleryDriveFolder = (eventId, folderUrlOrId, accessToken) =>
  request(`/api/client-galleries/${eventId}/import-drive-folder`, {
    method: "POST",
    headers: supabaseAdminHeaders(accessToken),
    body: JSON.stringify({ folderUrlOrId }),
  });
export const listClientGalleryAlbums = (eventId, accessToken) =>
  request(`/api/client-galleries/${eventId}/albums`, {
    headers: supabaseAdminHeaders(accessToken),
  });
export const createClientGalleryAlbum = (eventId, payload, accessToken) =>
  request(`/api/client-galleries/${eventId}/albums`, {
    method: "POST",
    headers: supabaseAdminHeaders(accessToken),
    body: JSON.stringify(payload),
  });
export const updateClientGalleryAlbum = (eventId, albumId, payload, accessToken) =>
  request(`/api/client-galleries/${eventId}/albums/${albumId}`, {
    method: "PATCH",
    headers: supabaseAdminHeaders(accessToken),
    body: JSON.stringify(payload),
  });
export const deleteClientGalleryAlbum = (eventId, albumId, accessToken) =>
  request(`/api/client-galleries/${eventId}/albums/${albumId}`, {
    method: "DELETE",
    headers: supabaseAdminHeaders(accessToken),
  });
export const importClientGalleryAlbumDriveFolder = (eventId, albumId, folderUrlOrId, accessToken) =>
  request(`/api/client-galleries/${eventId}/albums/${albumId}/import-drive-folder`, {
    method: "POST",
    headers: supabaseAdminHeaders(accessToken),
    body: JSON.stringify({ folderUrlOrId }),
  });
export const getAdminEvents = (password) =>
  request("/api/admin/events", { headers: { "x-gallery-admin-password": password } });
export const createAdminEvent = (password, event) =>
  request("/api/admin/events", {
    method: "POST",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(event),
  });
export const syncAdminEvent = (password, id, options = {}) =>
  request(`/api/admin/events/${id}/sync-drive`, {
    method: "POST",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(options),
  });
export const updateAdminEvent = (password, id, event) =>
  request(`/api/admin/events/${id}`, {
    method: "PUT",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(event),
  });
export const uploadAdminEventCover = (password, id, filePayload) =>
  request(`/api/admin/events/${id}/cover-image`, {
    method: "POST",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(filePayload),
  });
export const deleteAdminEvent = (password, id) =>
  request(`/api/admin/events/${id}`, {
    method: "DELETE",
    headers: { "x-gallery-admin-password": password },
  });
export const addAdminFolder = (password, eventId, folder) =>
  request(`/api/admin/events/${eventId}/folders`, {
    method: "POST",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(folder),
  });
export const updateAdminFolder = (password, eventId, folderId, folder) =>
  request(`/api/admin/events/${eventId}/folders/${folderId}`, {
    method: "PUT",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(folder),
  });
export const deleteAdminFolder = (password, eventId, folderId) =>
  request(`/api/admin/events/${eventId}/folders/${folderId}`, {
    method: "DELETE",
    headers: { "x-gallery-admin-password": password },
  });
export const getAdminPhotos = (password, eventId, folderId = "") =>
  request(`/api/admin/events/${eventId}/photos?limit=100${folderId ? `&folderId=${encodeURIComponent(folderId)}` : ""}`, {
    headers: { "x-gallery-admin-password": password },
  });
export const getAdminShareLinks = (password, eventId) =>
  request(`/api/admin/events/${eventId}/share-links`, {
    headers: { "x-gallery-admin-password": password },
  });
export const createAdminShareLink = (password, eventId, payload) =>
  request(`/api/admin/events/${eventId}/share-links`, {
    method: "POST",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(payload),
  });
export const updateAdminShareLink = (password, shareLinkId, payload) =>
  request(`/api/admin/gallery/share-links/${shareLinkId}`, {
    method: "PATCH",
    headers: { "x-gallery-admin-password": password },
    body: JSON.stringify(payload),
  });
export const deactivateAdminShareLink = (password, shareLinkId) =>
  request(`/api/admin/gallery/share-links/${shareLinkId}`, {
    method: "DELETE",
    headers: { "x-gallery-admin-password": password },
  });
export const getAdminAccessRegistrations = (password, eventId) =>
  request(`/api/admin/events/${eventId}/access-registrations`, {
    headers: { "x-gallery-admin-password": password },
  });
export const startAdminFaceIndex = (password, eventId) =>
  request(`/api/admin/events/${eventId}/face-index/start`, {
    method: "POST",
    headers: { "x-gallery-admin-password": password },
  });
export const getAdminFaceIndexStatus = (password, eventId) =>
  request(`/api/admin/events/${eventId}/face-index/status`, {
    headers: { "x-gallery-admin-password": password },
  });
export const reprocessAdminFaceSearch = (password, registrationId) =>
  request(`/api/admin/registrations/${registrationId}/face-search/reprocess`, {
    method: "POST",
    headers: { "x-gallery-admin-password": password },
  });
export const deleteAdminFaceData = (password, registrationId) =>
  request(`/api/admin/registrations/${registrationId}/face-data`, {
    method: "DELETE",
    headers: { "x-gallery-admin-password": password },
  });
export const downloadAdminRegistrationsCsv = (password, eventId, filename) =>
  downloadAdminCsv(password, `/api/admin/events/${eventId}/access-registrations.csv`, filename);

export const createGalleryZipJob = (slug, token, folderId = "", photoIds = [], bucket = "") =>
  request(`/api/gallery/${slug}/zip-jobs`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ folderId: folderId || null, photoIds, bucket }),
  });
export const getGalleryZipJob = (slug, token, jobId) =>
  request(`/api/gallery/${slug}/zip-jobs/${jobId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
